/**
 * Islamic and Arabic-inspired geometric patterns for screen backgrounds. Each is one repeating tile, drawn with
 * lines only (stroke = the pattern colour), so it stays crisp at any size and costs nothing to animate around.
 * Tiles use a 100-unit grid; `w`/`h` give the repeat size in those units.
 */

export type PatternId = 'khatam' | 'hexstar' | 'zellige' | 'circles' | 'lattice' | 'moroccan' | 'rosette' | 'kufic' | 'arches';
export type PatternCoverage = 'full' | 'border' | 'corners';

export interface PatternDef { id: PatternId; label: string; w: number; h: number; svg: string }

const r = (n: number) => +n.toFixed(2);
/** Points of a regular polygon/star around (cx, cy). */
function star(cx: number, cy: number, outer: number, inner: number, points: number, rot = -Math.PI / 2) {
  const pts: string[] = [];
  for (let i = 0; i < points * 2; i++) {
    const rad = i % 2 ? inner : outer, a = rot + (i * Math.PI) / points;
    pts.push(`${r(cx + Math.cos(a) * rad)},${r(cy + Math.sin(a) * rad)}`);
  }
  return `M${pts.join('L')}Z`;
}
function poly(cx: number, cy: number, rad: number, n: number, rot = 0) {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) { const a = rot + (i * 2 * Math.PI) / n; pts.push(`${r(cx + Math.cos(a) * rad)},${r(cy + Math.sin(a) * rad)}`); }
  return `M${pts.join('L')}Z`;
}
const path = (d: string) => `<path d="${d}"/>`;

// 8-point star (khatam) made of two squares, linked to its neighbours by a square lattice.
const khatam = (() => {
  const c = 50, s = 26;
  const sq1 = `M${c - s},${c - s}H${c + s}V${c + s}H${c - s}Z`;
  const d = s * Math.SQRT2;
  const sq2 = `M${c},${c - d}L${c + d},${c}L${c},${c + d}L${c - d},${c}Z`;
  const links = `M${c},${c - d}V0M${c},${c + d}V100M${c - d},${c}H0M${c + d},${c}H100`;
  const corners = [0, 100].flatMap((x) => [0, 100].map((y) => poly(x, y, 13, 8, Math.PI / 8))).join('');
  return path(sq1) + path(sq2) + path(links) + path(corners);
})();

// Six-point stars on a hexagonal grid (tile 100 × 173.2).
const hexstar = (() => {
  const h = 173.2;
  const at = [[50, 0], [0, h / 2], [100, h / 2], [50, h], [50, h / 2 - 0.001]].slice(0, 4);
  const stars = [[0, 0], [100, 0], [50, h / 2], [0, h], [100, h]].map(([x, y]) => star(x!, y!, 28, 16.2, 6)).join('');
  const hexes = [[50, 0], [0, h / 2], [100, h / 2], [50, h]].map(([x, y]) => poly(x!, y!, 16, 6)).join('');
  void at;
  return path(stars) + path(hexes);
})();

// Zellige: 8-point stars and crosses in the Moroccan manner.
const zellige = (() => {
  const s = star(50, 50, 34, 22, 8, -Math.PI / 2 + Math.PI / 8);
  const inner = poly(50, 50, 14, 8, Math.PI / 8);
  const cross = [0, 100].flatMap((x) => [0, 100].map((y) => `M${x - 10},${y}L${x},${y - 10}L${x + 10},${y}L${x},${y + 10}Z`)).join('');
  const links = 'M50,16V0M50,84V100M16,50H0M84,50H100';
  return path(s) + path(inner) + path(cross) + path(links);
})();

// Interlaced circles (each circle passes through its neighbours' centres).
const circles = (() => {
  const c = [[0, 0], [100, 0], [0, 100], [100, 100], [50, 50]];
  const big = c.map(([x, y]) => `M${x! - 50},${y}a50,50 0 1,0 100,0a50,50 0 1,0 -100,0`).join('');
  const small = [[50, 0], [0, 50], [100, 50], [50, 100]].map(([x, y]) => `M${x! - 20},${y}a20,20 0 1,0 40,0a20,20 0 1,0 -40,0`).join('');
  return path(big) + path(small);
})();

// Mashrabiya lattice: diamonds with an inner cross.
const lattice = 'M50,0L100,50L50,100L0,50Z M50,18L82,50L50,82L18,50Z M50,18V82M18,50H82 M0,0L18,18M100,0L82,18M0,100L18,82M100,100L82,82'.split(' ').map(path).join('');

// Moroccan quatrefoil trellis.
const moroccan = (() => {
  const q = (cx: number, cy: number) => `M${cx - 22},${cy}C${cx - 22},${cy - 18} ${cx - 8},${cy - 22} ${cx},${cy - 32}C${cx + 8},${cy - 22} ${cx + 22},${cy - 18} ${cx + 22},${cy}C${cx + 22},${cy + 18} ${cx + 8},${cy + 22} ${cx},${cy + 32}C${cx - 8},${cy + 22} ${cx - 22},${cy + 18} ${cx - 22},${cy}Z`;
  return path(q(50, 50) + q(0, 0) + q(100, 0) + q(0, 100) + q(100, 100));
})();

// Rosette: a 12-point star with petals.
const rosette = (() => {
  const s12 = star(50, 50, 40, 30, 12);
  const s6 = star(50, 50, 22, 13, 6);
  const ring = 'M50,6a44,44 0 1,0 0.01,0';
  const corners = [0, 100].flatMap((x) => [0, 100].map((y) => star(x, y, 9, 4, 4, Math.PI / 4))).join('');
  return path(s12) + path(s6) + path(ring) + path(corners);
})();

// Square Kufic-inspired meander (calligraphic blocks, not real words).
const kufic = 'M10,10H90V90H10V30H70V70H30V50H50 M0,50H10M90,50H100M50,0V10M50,90V100'.split(' ').map(path).join('');

// Mihrab arches in a row.
const arches = (() => {
  const a = (x: number) => `M${x + 8},100V48C${x + 8},28 ${x + 25},14 ${x + 50},4C${x + 75},14 ${x + 92},28 ${x + 92},48V100`;
  const inner = (x: number) => `M${x + 22},100V54C${x + 22},40 ${x + 34},30 ${x + 50},22C${x + 66},30 ${x + 78},40 ${x + 78},54V100`;
  return path(a(0)) + path(inner(0)) + path('M0,100H100');
})();

export const PATTERNS: PatternDef[] = [
  { id: 'khatam', label: 'Khatam star', w: 100, h: 100, svg: khatam },
  { id: 'zellige', label: 'Zellige', w: 100, h: 100, svg: zellige },
  { id: 'hexstar', label: 'Six-point stars', w: 100, h: 173.2, svg: hexstar },
  { id: 'rosette', label: 'Rosette', w: 100, h: 100, svg: rosette },
  { id: 'circles', label: 'Interlaced circles', w: 100, h: 100, svg: circles },
  { id: 'lattice', label: 'Mashrabiya', w: 100, h: 100, svg: lattice },
  { id: 'moroccan', label: 'Moroccan trellis', w: 100, h: 100, svg: moroccan },
  { id: 'kufic', label: 'Kufic blocks', w: 100, h: 100, svg: kufic },
  { id: 'arches', label: 'Mihrab arches', w: 100, h: 100, svg: arches },
];
export const patternById = (id: unknown) => PATTERNS.find((p) => p.id === id) ?? null;

/** Repeat size (px at 1080p) for each size setting. */
export const PATTERN_SIZES = { small: 70, medium: 120, large: 200 } as const;
export type PatternSize = keyof typeof PATTERN_SIZES;

export interface BackgroundPattern {
  id: PatternId;
  color: string;
  /** 0–100. */
  opacity: number;
  coverage: PatternCoverage;
  size: PatternSize;
}

/** A small, self-contained SVG of a pattern, for previews in the Studio. */
export function patternPreviewSvg(id: PatternId, color = '#FFFFFF', bg = 'transparent'): string {
  const p = patternById(id)!;
  const sc = 0.42;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 80"><rect width="120" height="80" fill="${bg}"/><defs><pattern id="p" patternUnits="userSpaceOnUse" width="${p.w * sc}" height="${p.h * sc}"><g transform="scale(${sc})" fill="none" stroke="${color}" stroke-width="3">${p.svg}</g></pattern></defs><rect width="120" height="80" fill="url(#p)"/></svg>`;
}
